import { useCallback, useEffect, useMemo } from 'react';

import { SPDX_3_EXPORT } from '../../../../shared/featureFlags/flagNames';
import { useParams, useQuery } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { isValidReportFormat } from '../types';
import type { AnyReportFormatOption, ReportsV3Params, ReportsV3QueryParams, ReportType, ReportV3Format } from '../types';

/**
 * Giant map of the possible format options for each report type.
 * All of these are stored as SelectOptions, so they can be easily used in the dropdown.
 */
export const formatOptionsMap: Readonly<Record<ReportType, AnyReportFormatOption[]>> = {
  licensing: [
    { label: 'HTML', value: 'HTML' },
    { label: 'PDF', value: 'PDF' },
    { label: 'CSV', value: 'CSV' },
    { label: 'Markdown', value: 'MD' },
    { label: 'Plain Text', value: 'TXT' },
  ],
  sbom: [
    { label: 'SPDX', value: 'SPDX' },
    { label: 'SPDX (JSON)', value: 'SPDX_JSON' },
    { label: 'CycloneDX (JSON)', value: 'CYCLONEDX_JSON' },
    { label: 'CycloneDX (XML)', value: 'CYCLONEDX_XML' },
  ],
  vulnerability: [
    { label: 'PDF', value: 'PDF' },
  ],
  remediationGuidance: [
    { label: 'PDF', value: 'PDF' },
    { label: 'HTML', value: 'HTML' },
    { label: 'JSON', value: 'JSON' },
  ],
};

/**
 * The sbom options with SPDX 3.0 export on. With three SPDX entries each names its version;
 * without the flag there is one SPDX version, so `formatOptionsMap` keeps the unversioned labels.
 */
const spdx3SbomFormatOptions: AnyReportFormatOption[] = [
  { label: 'SPDX 2.3', value: 'SPDX' },
  { label: 'SPDX 2.3 (JSON)', value: 'SPDX_JSON' },
  { label: 'SPDX 3.0 (JSON)', value: 'SPDX3_JSON' },
  { label: 'CycloneDX (JSON)', value: 'CYCLONEDX_JSON' },
  { label: 'CycloneDX (XML)', value: 'CYCLONEDX_XML' },
];

/**
 * The format options the selector offers for a report type.
 */
export const getFormatOptions = (reportType: ReportType, isSpdx3ExportEnabled: boolean): AnyReportFormatOption[] =>
  reportType === 'sbom' && isSpdx3ExportEnabled ? spdx3SbomFormatOptions : formatOptionsMap[reportType];

/**
 * The label the format selector shows for a format, e.g. "CycloneDX (JSON)" for CYCLONEDX_JSON.
 */
export const getFormatLabel = (reportType: ReportType, format: ReportV3Format, isSpdx3ExportEnabled: boolean): string =>
  getFormatOptions(reportType, isSpdx3ExportEnabled).find((option) => option.value === format)?.label ?? format;

/**
 * Returns the first format option for the given report type.
 * @param reportType The report type to get the first format option for.
 * @param isSpdx3ExportEnabled Whether the `spdx3Export` flag is on.
 * @returns The first format option for the given report type.
 */
const firstFormatForReportType = (reportType: ReportType, isSpdx3ExportEnabled: boolean): AnyReportFormatOption => {
  // Cast is safe here, we know these arrays are non-empty
  return (getFormatOptions(reportType, isSpdx3ExportEnabled)[0] as AnyReportFormatOption);
};

/**
 * Hook for managing the format selector for a report.
 * It reads the URL query params for the format, and ensures that it is always set and is always valid for the report type.
 * If it's not, it sets the first valid format for the report type.
 * It returns the options and value for the format selector, and a function to set the format.
 */
export const useReportFormatSelector = () => {
  const { reportType } = useParams<ReportsV3Params>();
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();
  const isSpdx3ExportEnabled = useIsFeatureEnabled(SPDX_3_EXPORT);

  const setFormat = useCallback((format: AnyReportFormatOption | null) => {
    if (format && isValidReportFormat(reportType, format.value, isSpdx3ExportEnabled)) {
      setQuery({ ...query, format: format.value });
    }
  }, [isSpdx3ExportEnabled, query, reportType, setQuery]);

  // Ensure that the format is always set and is always valid for the report type.
  // If it's not, set the first valid format for the report type.
  useEffect(() => {
    if (!query.format || !isValidReportFormat(reportType, query.format, isSpdx3ExportEnabled)) {
      setFormat(firstFormatForReportType(reportType, isSpdx3ExportEnabled));
    }
  }, [isSpdx3ExportEnabled, query, reportType, setFormat, setQuery]);

  const { options, value } = useMemo(() => {
    const optionsForReportType = getFormatOptions(reportType, isSpdx3ExportEnabled);
    return {
      options: optionsForReportType,
      value: optionsForReportType
        .find((option) => option.value === query.format) ?? firstFormatForReportType(reportType, isSpdx3ExportEnabled),
    };
  }, [isSpdx3ExportEnabled, reportType, query.format]);

  return {
    options,
    value,
    setFormat,
  };
};
